<template>
  <div :id="id">
    <h1 v-if="title != undefined" class="text-muted" id="form-title">
      {{ title }}
    </h1>
    <form v-on:submit.prevent="__submit">
      <!-- Contrôles -->
      <slot />

      <!-- Boutons -->
      <b-row v-if="showButtons" class="btn-row no-gutters">
        <button
          class="btn btn-primary"
          type="submit"
          :name="__computedSubmitBtnName"
          :id="computedSubmitBtnId"
        >
          {{ "components.generic.GenericForm.submit" | i18n }}
        </button>
        <button
          class="btn btn-danger"
          v-if="cancelable"
          type="button"
          @click="__cancel"
          :name="__computedCancelBtnName"
        >
          {{ "components.generic.GenericForm.cancel" | i18n }}
        </button>
      </b-row>
    </form>
  </div>
</template>

<script>
/**
 * @description Composant générique pour les formulaires
 * @author Alexandre Gallant
 * @vue-prop {String} title - Titre du formulaire
 * @vue-prop {Function} action - Méthode à appeler « onSubmit »
 * @vue-prop {Function} canSubmit - Méthode à appeler pour savoir si le contenu des contrôles est valide
 * @vue-prop {Boolean} cancelable - Si le formulaire doit inclure un bouton « annuler »
 * @vue-prop {Object} otherValues - Valeurs qui doivent être envoyées dans la requête et qui ne sont pas dans le formulaire
 * @vue-prop {Number} actionId - Id à utiliser pour les requêtes sur les articles existants
 * @vue-prop {Boolean} showButtons - Si l'on doit afficher les boutons
 * @vue-prop {String} id - Id à donner au formulaire
 * @vue-data {Boolean} submitting - Si les données sont en train d'être envoyées
 * @vue-computed {String} __computedSubmitBtnName - Constante pour le nom du bouton « submit »
 * @vue-computed {String} __computedCancelBtnName - Constante pour le nom du bouton « annuler »
 * @vue-computed {String} computedSubmitBtnId - L'id du bouton submit
 * @vue-computed {Function} computedKeyDownEventListener - Le listener pour l'évènement «keydown»
 * @vue-event {void} canceled - Est émis quand l'utilisateur clique sur le bouton « annuler »
 * @vue-event {void} validationRequested - Est émis quand l'utilisateur soumet le formulaire
 * @vue-event {String} submited - Est émis quand l'utilisateur soumet le formulaire
 */
export default {
  name: "GenericForm",
  props: {
    title: {
      type: String,
      required: false
    },
    action: {
      type: Function,
      required: true
    },
    canSubmit: {
      type: Function,
      required: true
    },
    cancelable: {
      type: Boolean,
      required: false,
      default: true
    },
    otherValues: {
      type: Object,
      required: false,
      default: () => {
        return {};
      }
    },
    actionId: {
      type: Number,
      required: false
    },
    showButtons: {
      type: Boolean,
      required: false,
      default: true
    },
    id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      submitting: false
    };
  },
  computed: {
    __computedSubmitBtnName() {
      return "submitBtn";
    },
    __computedCancelBtnName() {
      return "cancelBtn";
    },
    computedSubmitBtnId() {
      return this.id + "-submit-btn";
    },
    computedKeyDownEventListener() {
      return event => {
        if (event.code == "Enter") {
          document.getElementById(this.computedSubmitBtnId).click();
        }
      };
    }
  },
  methods: {
    /**
     * @description Appelle l'événement « canceled »
     */
    __cancel() {
      this.$emit("canceled");
    },
    /**
     * @description Soumet le formulaire si le contenu des contrôles est valide
     * @param {Object} event L'événement ayant mené à l'appel de cette méthode
     */
    async __submit(event) {
      if (this.submitting) {
        return;
      }
      this.submitting = true;
      event.preventDefault();
      this.$emit("validationRequested");
      if (!this.canSubmit()) {
        this.submitting = false;
        return;
      }
      let result;

      if (this.actionId == undefined) {
        result = await this.action(
          this.__convertToJson(this.__extractValues(event))
        );
      } else {
        result = await this.action(
          this.actionId,
          this.__convertToJson(this.__extractValues(event))
        );
      }

      this.$emit("submited", result);
      if (result != undefined && result != null && result.status == 200) {
        location.reload();
      }
      this.submitting = false;
    },
    /**
     * @description Extrait le contenu des contrôles
     * @param {Object} event L'événement ayant mené à l'appel de cette méthode
     * @returns {Object} Les valeurs extraites des contrôles
     */
    __extractValues(event) {
      const elements = event.srcElement.elements;
      var values = {};
      for (const index in elements) {
        if (!this.__isValidInput(elements[index])) {
          continue;
        }
        if (elements[index].type === "checkbox") {
          values[elements[index].name] = elements[index].checked;
          continue;
        }

        if (!isNaN(elements[index].value)) {
          values[elements[index].name] = Number(elements[index].value);
        } else if (elements[index].value === "true") {
          values[elements[index].name] = true;
        } else if (elements[index].value === "false") {
          values[elements[index].name] = false;
        } else {
          values[elements[index].name] = elements[index].value;
        }
      }
      for (const key in this.otherValues) {
        values[key] = this.otherValues[key];
      }
      return values;
    },
    /**
     * @description Convertit l'objet donné en Json
     * @param {Object} values Les valeurs à convertir en Json
     * @returns {String} Les valeurs converties
     */
    __convertToJson(values) {
      return JSON.stringify(values);
    },
    /**
     * @description Vérifie si la balise donnée est un input dont la valeur doit être prise en compte
     * @param {Object} element La balise à vérifier
     * @returns {Boolean} S'il s'agit d'un input dont la valeur doit être prise en compte
     */
    __isValidInput(element) {
      return (
        element.name != undefined &&
        element.value != undefined &&
        element.name != this.__computedSubmitBtnName &&
        element.name != this.__computedCancelBtnName
      );
    }
  },
  /**
   * @description Crée un event listener pour « submit » quand l'utilisateur appuie sur la touche « entrer »
   */
  mounted() {
    window.addEventListener("keydown", this.computedKeyDownEventListener);
  },
  /**
   * @description Détruit l'event listener
   */
  beforeDestroy() {
    window.removeEventListener("keydown", this.computedKeyDownEventListener);
  }
};
</script>

<style lang="scss" scoped>
.form-row {
  margin-left: 0;
  margin-right: 0;
}

.btn-row {
  margin-top: 42px;
}

.btn {
  border-radius: 9px !important;
  height: 40px !important;
  padding-left: 24px !important;
  padding-right: 24px !important;
}

.btn-danger {
  margin-left: 26px;
}

#form-title {
  margin-bottom: 37px;
}
</style>
